import { CommonModule } from "@angular/common";
import {
  ChangeDetectionStrategy,
  Component,
  inject,
  isDevMode,
  OnDestroy,
  OnInit
} from "@angular/core";
import { Router, RouterModule } from "@angular/router";
import { Store } from "@ngxs/store";
import * as ApplicationStateActions from "@vp/data-access/application";
import { ApplicationState } from "@vp/data-access/application";
import { User } from "@vp/models";
import { BottomNavShellModule } from "@vp/shared/bottom-nav/ui/bottom-nav-shell";
import { SecurityPopupModule } from "@vp/shared/components/security-popup";
import { FlexModule } from "@vp/shared/directives/flex";
import { LoggerService, ON_INIT } from "@vp/shared/logger-service";
import { SignalRApiService, SignalREventService } from "@vp/shared/signal-r-service";
import { SystemBannerModule } from "@vp/shared/system-banner";
import { ANON_CONFIG } from "@vp/shared/tokens";
import { TopNavLogoModule } from "@vp/shared/top-nav/ui/top-nav-logo";
import { TopNavShellModule } from "@vp/shared/top-nav/ui/top-nav-shell";
import { Observable, Subject } from "rxjs";
import { takeUntil, tap } from "rxjs/operators";

@Component({
  selector: "vp-app-root",
  templateUrl: "./app.component.html",
  styleUrls: ["./app.component.scss"],
  standalone: true,
  imports: [
    TopNavShellModule,
    TopNavLogoModule,
    BottomNavShellModule,
    SystemBannerModule,
    RouterModule,
    SecurityPopupModule,
    FlexModule,
    CommonModule
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AppComponent implements OnInit, OnDestroy {
  private static readonly NAME = "AppComponent";

  loggedInUser$: Observable<User | null> = inject(Store).select(ApplicationState.loggedInUser);
  isBrowserOnline$: Observable<boolean> = inject(Store).select(ApplicationState.isBrowserOnline);

  private readonly logger = inject(LoggerService);
  private readonly router = inject(Router);
  private readonly signalREventService = inject(SignalREventService);
  private readonly signalRApiService = inject(SignalRApiService);
  private readonly store = inject(Store);
  private readonly anonConfig = inject(ANON_CONFIG);
  readonly isAnonApi = !!this.anonConfig;

  isProd = !isDevMode();
  isIframe = false;
  isLoggedIn = false;
  isAdmin = false;

  get isPublicPage() {
    return this.isAnonApi || this.router.url.startsWith("/clinical-informatics-support");
  }

  private destroyed$ = new Subject<void>();

  ngOnInit() {
    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "visible") {
        this.signalRApiService.reconnect();
      }
    });

    window.addEventListener("focus", () => {
      this.signalRApiService.reconnect();
    });

    window.addEventListener("offline", () => {
      this.store.dispatch(
        new ApplicationStateActions.PatchState({
          isBrowserOnline: false
        })
      );
    });

    window.addEventListener("online", () => {
      this.store.dispatch(
        new ApplicationStateActions.PatchState({
          isBrowserOnline: true
        })
      );
      this.signalRApiService.reconnect();
    });

    //this.appInsightsService.logEvent("Breakout app initalized");
    this.isIframe = window !== window.parent && !window.opener;

    if (this.isAnonApi) {
      switch (this.anonConfig.origin) {
        case "VCLINICALINFORMATICIST":
          this.router.navigate(["/clinical-informatics-support"]);
          break;
        case "VCONSULT":
          this.router.navigate(["/wizard"], { queryParams: { returnUrl: "vconsult-submitted" } });
          break;
        default:
          this.router.navigate(["/wizard"]);
      }
    }

    this.loggedInUser$
      .pipe(
        tap((user: User | null) => {
          this.isLoggedIn = !!user;
          this.isAdmin = false;
          // TODO put into app store service
          if (user && user.roles) {
            for (const role of user.roles) {
              if (role.roleId === user.selectedRoleId && role.friendlyId === "admin") {
                this.isAdmin = true;
              }
            }
            this.signalREventService.listen();
          } else {
            this.signalREventService.stopListening();
          }
        }),
        takeUntil(this.destroyed$)
      )
      .subscribe({
        next: (user: User | null) => {
          this.logger.systemEvent(
            `${AppComponent.NAME}.${ON_INIT}.loggedInUser`,
            "Logged In User Context Changed",
            {
              user: { userId: user?.userId } as Pick<User, "userId"> // TODO: Do we want to log any other information from user here?
            }
          );
        }
      });
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
  }
}
